import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { VUKZ } from '../theme/vukzTheme';
import { getBiomechanicsSession, getRaceContext } from '../services/biomechanicsService';
import { normalizeSession } from '../utils/normalizeSession';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, AreaChart, Area, ScatterChart, Scatter, ComposedChart, Bar,
} from 'recharts';
import {
  ArrowLeft, Activity, Timer, Waves, Zap, Eye, Link2, Video,
  AlertTriangle, CheckCircle2, Loader2, ChevronRight, Trophy,
  Clock, Target, Ruler, Droplets, BarChart3, TrendingUp,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   Design Tokens
   ═══════════════════════════════════════════════════════════════════════════════ */
const T = {
  bg:         '#fafafa',
  card:       '#ffffff',
  text:       '#111111',
  textSoft:   '#6b7280',
  textMute:   '#9ca3af',
  border:     '#e5e7eb',
  accent:     '#111111',
  accentSoft: '#f3f4f6',
  success:    '#16a34a',
  successBg:  '#f0fdf4',
  warning:    '#f59e0b',
  warningBg:  '#fffbeb',
  error:      '#dc2626',
  errorBg:    '#fef2f2',
  info:       '#2563eb',
  infoBg:     '#eff6ff',
  cyan:       '#0891b2',
  cyanBg:     '#ecfeff',
  purple:     '#7c3aed',
  purpleBg:   '#f5f3ff',
  // Chart-specific
  strokeLine:    '#111111',
  intervalLine:  '#f59e0b',
  reactionLine:  '#0284c7',
  splitLine:     '#0ea5e9',
  totalLine:     '#1d4ed8',
  gapFill:       '#fef2f2',
  okFill:        '#f0fdf4',
};

const STYLE_LABELS = {
  freestyle: 'Livre', backstroke: 'Costas', butterfly: 'Borboleta',
  breaststroke: 'Peito', medley: 'Medley',
};

/* ── Helpers ───────────────────────────────────────────────────────────────── */
const fmtTime = (s) => {
  if (s == null || isNaN(s)) return '—';
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
};

const fmtShort = (s) => {
  if (s == null || isNaN(s)) return '—';
  return `${s.toFixed(2)}s`;
};

/* ── Summary Card ──────────────────────────────────────────────────────────── */
const SummaryCard = ({ icon: Icon, label, value, unit, color, bg, highlight }) => (
  <div className={`rounded-xl border p-3 transition-all ${highlight ? 'ring-2 ring-offset-1' : ''}`}
    style={{
      borderColor: highlight ? color : T.border,
      backgroundColor: T.card,
      ringColor: highlight ? color : undefined,
    }}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={13} style={{ color: color || T.textSoft }} />
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>{label}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-lg font-black" style={{ color: T.text }}>{value}</span>
      {unit && <span className="text-[10px] font-semibold" style={{ color: T.textMute }}>{unit}</span>}
    </div>
  </div>
);

/* ── Custom Tooltip ────────────────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label, type }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  const status = point?.status;
  const isGap = status === 'missed';
  const isSuspect = status === 'suspect';
  const isOvercount = status === 'overcount';
  const isFirst = status === 'first';

  return (
    <div className="rounded-xl border shadow-xl px-3.5 py-2.5 text-xs min-w-[160px]"
      style={{
        borderColor: isGap ? T.error : isSuspect ? T.warning : T.border,
        borderWidth: isGap || isSuspect ? 2 : 1,
        backgroundColor: isGap ? T.errorBg : isSuspect ? T.warningBg : '#fff',
      }}>
      <p className="font-bold mb-1" style={{ color: T.text }}>
        {type === 'cumulative'
          ? `Braçada ${label}`
          : `Braçada #${point?.number || '?'} · ${fmtTime(label)}`
        }
      </p>
      {type === 'interval' && isGap && (
        <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded-lg"
          style={{ backgroundColor: T.error + '18' }}>
          <span style={{ color: T.error, fontSize: 14 }}>⚠</span>
          <div>
            <p className="font-black text-[10px]" style={{ color: T.error }}>Braçada Perdida</p>
            <p className="text-[9px]" style={{ color: T.error + 'cc' }}>
              Gap de {fmtShort(point?.interval)} — provável falha de detecção
            </p>
          </div>
        </div>
      )}
      {type === 'interval' && isSuspect && (
        <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded-lg"
          style={{ backgroundColor: T.warning + '18' }}>
          <span style={{ color: T.warning, fontSize: 14 }}>⚡</span>
          <p className="font-bold text-[10px]" style={{ color: T.warning }}>Intervalo suspeito</p>
        </div>
      )}
      {type === 'interval' && isOvercount && (
        <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded-lg"
          style={{ backgroundColor: T.purple + '18' }}>
          <span style={{ color: T.purple, fontSize: 14 }}>✦</span>
          <p className="font-bold text-[10px]" style={{ color: T.purple }}>Possível braçada extra (eco)</p>
        </div>
      )}
      {type === 'interval' && isFirst && (
        <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded-lg"
          style={{ backgroundColor: T.cyan + '18' }}>
          <span style={{ color: T.cyan, fontSize: 14 }}>🏊</span>
          <p className="font-bold text-[10px]" style={{ color: T.cyan }}>Saída → 1ª braçada</p>
        </div>
      )}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">
            {type === 'cumulative' ? fmtTime(p.value) : fmtShort(p.value)}
          </span>
          {type === 'interval' && <span className="ml-1 opacity-60">s</span>}
        </p>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════════ */
const RaceDashboardPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [raceCtx, setRaceCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [sessResp, ctxResp] = await Promise.all([
          getBiomechanicsSession(sessionId),
          getRaceContext(sessionId),
        ]);
        const sessData = sessResp.data?.data?.session || sessResp.data?.data || sessResp.data;
        setSession(normalizeSession(sessData));
        const ctxData = ctxResp.data?.data || ctxResp.data;
        setRaceCtx(ctxData?.linked ? ctxData.context : null);
      } catch (err) {
        console.error('[RaceDash] fetch error:', err);
        setError(err.message || 'Erro ao carregar sessão');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [sessionId]);

  // ── Derived data ────────────────────────────────────────────────
  const strokeTiming = session?.strokeTiming;
  const strokes = strokeTiming?.strokes || [];
  const summary = strokeTiming?.summary || {};
  const reaction = session?.reactionTimeOfficial;
  const totalOfficial = session?.totalTimeOfficial;
  const splitsOfficial = session?.splitsOfficial || [];
  const startToFirst = session?.startToFirstStrokeS;
  const largestGap = session?.largestGapS;
  const raceDuration = session?.raceDurationSeconds;
  const style = session?.swimmingStyle;
  const strokeCount = session?.canonicalStrokeCount || 0;

  // Time origin: use race expected start or first stroke minus tolerance
  const raceOrigin = useMemo(() => {
    if (summary.expectedStartS && summary.expectedStartS > 0) return summary.expectedStartS;
    if (strokes.length > 0) return Math.max(0, strokes[0].videoTimeS - 2);
    return 0;
  }, [summary, strokes]);

  // ── Build chart data ────────────────────────────────────────────
  const { cumulativeData, intervalData } = useMemo(() => {
    if (!strokes.length) return { cumulativeData: [], intervalData: [] };

    const cum = [];
    const intv = [];

    // Origin point at race start
    cum.push({ timeS: 0, strokes: 0 });

    strokes.forEach((s, i) => {
      const t = +(s.videoTimeS - raceOrigin).toFixed(3);
      cum.push({ timeS: t, strokes: s.number });

      const iv = s.intervalS;
      // First stroke: interval from start to first stroke
      if (i === 0) {
        const firstInterval = startToFirst != null ? startToFirst : (s.videoTimeS - raceOrigin);
        intv.push({
          timeS: t,
          interval: +firstInterval.toFixed(3),
          status: 'first',
          label: `Saída → 1ª braçada`,
        });
      } else if (iv != null) {
        // Classify
        const median = summary.medianIntervalS || summary.avgIntervalS || 1;
        let status = 'ok';
        if (iv > median * 2.5) status = 'missed';
        else if (iv > median * 1.8) status = 'suspect';
        else if (iv < median * 0.4) status = 'overcount';

        intv.push({
          timeS: t,
          interval: +iv.toFixed(3),
          status,
          label: `Braçada ${s.number}`,
        });
      }
    });

    return { cumulativeData: cum, intervalData: intv };
  }, [strokes, raceOrigin, startToFirst, summary]);

  // ── Official reference lines (relative to race origin) ──────────
  const officialLines = useMemo(() => {
    const lines = [];
    if (reaction != null) {
      lines.push({ x: reaction, label: `Reação ${fmtShort(reaction)}`, color: T.reactionLine, dash: '6 3' });
    }
    splitsOfficial.forEach(sp => {
      if (sp.timeS != null) {
        lines.push({ x: sp.timeS, label: sp.distance, color: T.splitLine, dash: '4 4' });
      }
    });
    if (totalOfficial != null) {
      lines.push({ x: totalOfficial, label: `Final ${fmtTime(totalOfficial)}`, color: T.totalLine, dash: '0' });
    }
    return lines;
  }, [reaction, splitsOfficial, totalOfficial]);

  // Interval thresholds for coloring
  const medianInterval = summary.medianIntervalS || summary.avgIntervalS || 1;

  // ── Loading / Error states ──────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <Loader2 className="animate-spin" size={28} style={{ color: T.accent }} />
        <span className="ml-3 text-sm font-semibold" style={{ color: T.textSoft }}>Carregando prova...</span>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} style={{ color: T.warning }} className="mx-auto mb-3" />
          <p className="text-sm font-semibold" style={{ color: T.text }}>{error || 'Sessão não encontrada'}</p>
          <Link to="/swim-vision" className="mt-3 inline-block px-4 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: T.accent }}>
            ← Voltar ao Swim Vision
          </Link>
        </div>
      </div>
    );
  }

  const hasStrokeData = strokes.length > 0;
  const hasOfficialData = reaction != null || totalOfficial != null || splitsOfficial.length > 0;
  const lane = raceCtx?.lanes?.[0];

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 space-y-5">

        {/* ── Breadcrumb ────────────────────────────────────────── */}
        <div className="flex items-center gap-2 text-xs" style={{ color: T.textMute }}>
          <Link to="/swim-vision" className="hover:underline flex items-center gap-1">
            <Eye size={12} /> Swim Vision
          </Link>
          <ChevronRight size={10} />
          <Link to="/swim-vision/dashboard" className="hover:underline">Dashboard</Link>
          <ChevronRight size={10} />
          <span className="font-bold" style={{ color: T.text }}>Prova #{sessionId}</span>
        </div>

        {/* ══════════════════════════════════════════════════════════
            BLOCO 1 — CABEÇALHO DA PROVA
            ══════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: T.border }}>
          {/* Top banner */}
          <div className="px-5 py-4 flex items-center justify-between"
            style={{ background: `linear-gradient(135deg, ${T.accent} 0%, #374151 100%)` }}>
            <div className="flex items-center gap-3">
              <Link to="/swim-vision" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                <ArrowLeft size={18} style={{ color: '#ffffff' }} />
              </Link>
              <div>
                <h1 className="text-lg font-black text-white">
                  Dashboard da Prova
                </h1>
                <p className="text-[11px] text-white/60">
                  Sessão #{sessionId} · {STYLE_LABELS[style] || style}
                </p>
              </div>
            </div>
            {hasOfficialData && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10">
                <Link2 size={12} style={{ color: '#60a5fa' }} />
                <span className="text-[10px] font-bold text-white/80">Dados oficiais vinculados</span>
              </div>
            )}
          </div>

          {/* Race context cards */}
          <div className="p-5">
            {raceCtx ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <InfoChip icon={Trophy} label="Competição" value={raceCtx.competition_name} />
                <InfoChip icon={Activity} label="Prova" value={raceCtx.event_name} />
                <InfoChip icon={Target} label="Fase" value={raceCtx.phase} />
                <InfoChip icon={Waves} label="Estilo" value={STYLE_LABELS[style] || style} />
                {lane && <InfoChip icon={Ruler} label="Raia" value={lane.lane_number} />}
                {lane && <InfoChip icon={Droplets} label="Atleta" value={lane.athlete_name} />}
              </div>
            ) : (
              <div className="p-3 rounded-xl text-xs text-center" style={{ backgroundColor: T.warningBg, color: T.warning }}>
                <AlertTriangle size={14} className="inline mr-1" />
                Prova não vinculada a competição.
                <span className="font-bold ml-1">Vincule em Swim Vision</span> para dados completos.
              </div>
            )}

            {/* Official result row */}
            {hasOfficialData && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 p-3 rounded-xl border"
                style={{ borderColor: '#bae6fd', backgroundColor: '#f0f9ff' }}>
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.cyan }}>Reação</span>
                  <p className="text-sm font-black" style={{ color: T.text }}>{fmtShort(reaction)}</p>
                </div>
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.cyan }}>Tempo Final</span>
                  <p className="text-sm font-black" style={{ color: T.text }}>{fmtTime(totalOfficial)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.cyan }}>Splits</span>
                  <div className="flex flex-wrap gap-2 mt-0.5">
                    {splitsOfficial.map((sp, i) => (
                      <span key={i} className="text-xs font-bold px-2 py-0.5 rounded"
                        style={{ backgroundColor: '#e0f2fe', color: T.info }}>
                        {sp.distance}: {fmtTime(sp.timeS)}
                      </span>
                    ))}
                    {splitsOfficial.length === 0 && <span className="text-[10px]" style={{ color: T.textMute }}>—</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Session + video links */}
            <div className="flex items-center gap-3 mt-3 text-[10px]" style={{ color: T.textMute }}>
              {(session?.videoBlobUrl || session?.video_blob_url) && (
                <span className="flex items-center gap-1">
                  <Video size={10} /> {session.video_filename || 'Video'}
                </span>
              )}
              {session?.strokeChartUrl && (
                <a href={session.strokeChartUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline" style={{ color: T.info }}>
                  <BarChart3 size={10} /> Chart PNG
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            BLOCO 2 — CARDS DE RESUMO
            ══════════════════════════════════════════════════════════ */}
        {hasStrokeData && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <SummaryCard icon={Zap} label="Saída → 1ª" color={T.cyan} bg={T.cyanBg}
              value={fmtShort(startToFirst)} highlight />
            <SummaryCard icon={Timer} label="Média" color={T.accent}
              value={fmtShort(summary.avgIntervalS)} unit="entre braç." />
            <SummaryCard icon={Clock} label="Mediana" color={T.purple} bg={T.purpleBg}
              value={fmtShort(summary.medianIntervalS)} />
            <SummaryCard icon={AlertTriangle} label="Maior gap" color={T.error} bg={T.errorBg}
              value={fmtShort(largestGap || summary.maxIntervalS)} highlight={largestGap > medianInterval * 2.5} />
            <SummaryCard icon={CheckCircle2} label="Menor gap" color={T.success} bg={T.successBg}
              value={fmtShort(summary.minIntervalS)} />
            <SummaryCard icon={Activity} label="Braçadas" color={T.accent}
              value={strokeCount} unit="no gráfico" />
            <SummaryCard icon={TrendingUp} label="Duração" color={T.info} bg={T.infoBg}
              value={fmtTime(raceDuration)} />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            BLOCO 3 — GRÁFICO: EVOLUÇÃO ACUMULADA
            ══════════════════════════════════════════════════════════ */}
        {hasStrokeData && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} style={{ color: T.accent }} />
              <h2 className="text-sm font-black" style={{ color: T.text }}>
                Tempo Acumulado por Braçada
              </h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ backgroundColor: T.accentSoft, color: T.textSoft }}>
                {strokes.length} braçadas · {fmtTime(cumulativeData.length > 1 ? cumulativeData[cumulativeData.length - 1].timeS : raceDuration)}
              </span>
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={cumulativeData} margin={{ top: 10, right: 20, left: 15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis
                  dataKey="strokes"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 10, fill: T.textMute }}
                  label={{ value: 'Braçada', position: 'insideBottomRight', offset: -5, fontSize: 10, fill: T.textMute }}
                />
                <YAxis
                  dataKey="timeS"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(v) => fmtTime(v)}
                  tick={{ fontSize: 10, fill: T.textMute }}
                  label={{ value: 'Tempo (s)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: T.textMute }}
                />
                <Tooltip content={<ChartTooltip type="cumulative" />} />

                {/* Official reference lines (now horizontal on Y axis = time) */}
                {officialLines.map((ol, i) => (
                  <ReferenceLine key={i} y={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1.5}
                    label={{ value: ol.label, position: 'right', fontSize: 9, fill: ol.color, fontWeight: 700 }} />
                ))}

                {/* Time accumulation line */}
                <Line type="stepAfter" dataKey="timeS" name="Tempo"
                  stroke={T.strokeLine} strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mt-2 text-[10px]" style={{ color: T.textMute }}>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.strokeLine }} /> Tempo acumulado</span>
              {reaction != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.reactionLine, borderBottom: '1px dashed' + T.reactionLine }} /> Reação</span>}
              {splitsOfficial.length > 0 && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.splitLine }} /> Splits</span>}
              {totalOfficial != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.totalLine }} /> Final</span>}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            BLOCO 4 — GRÁFICO: INTERVALOS ENTRE BRAÇADAS (Enhanced v2)
            ══════════════════════════════════════════════════════════ */}
        {hasStrokeData && intervalData.length > 0 && (
          <IntervalChart
            intervalData={intervalData}
            medianInterval={medianInterval}
            officialLines={officialLines}
          />
        )}

        {/* ══════════════════════════════════════════════════════════
            BLOCO 5 — TABELA BRAÇADA A BRAÇADA
            ══════════════════════════════════════════════════════════ */}
        {hasStrokeData && (
          <StrokeTable strokes={strokes} raceOrigin={raceOrigin} medianInterval={medianInterval}
            startToFirst={startToFirst} />
        )}

        {/* ══════════════════════════════════════════════════════════
            BLOCO 6 — CHART PNG (se disponível)
            ══════════════════════════════════════════════════════════ */}
        {session?.strokeChartUrl && (
          <div className="bg-white rounded-2xl border p-5" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                Chart Gerado pelo Worker (PNG)
              </span>
            </div>
            <img src={session.strokeChartUrl} alt="Stroke timing chart"
              className="w-full rounded-xl border" style={{ borderColor: T.border }} />
          </div>
        )}

        {/* No data state */}
        {!hasStrokeData && (
          <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: T.border }}>
            <Timer size={40} className="mx-auto mb-3" style={{ color: T.textMute }} />
            <p className="text-sm font-bold" style={{ color: T.text }}>Dados de timing não disponíveis</p>
            <p className="text-xs mt-1" style={{ color: T.textSoft }}>
              A sessão precisa ser processada com dados de braçada (stroke timing) para exibir este dashboard.
            </p>
            <Link to={`/swim-vision?session=${sessionId}`}
              className="inline-block mt-4 px-4 py-2 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: T.accent }}>
              Ver sessão no Swim Vision
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Info Chip (for header grid) ───────────────────────────────────────────── */
const InfoChip = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 rounded-lg p-2.5">
    <div className="flex items-center gap-1 mb-0.5">
      <Icon size={10} style={{ color: T.textMute }} />
      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.textMute }}>{label}</span>
    </div>
    <p className="text-xs font-bold truncate" style={{ color: T.text }}>
      {value ?? '—'}
    </p>
  </div>
);

/* ── CSS Keyframes for pulsing animation on gap dots ────────────────────── */
const pulseStyle = `
@keyframes pulse-ring {
  0%   { r: 5; opacity: 0.8; }
  100% { r: 14; opacity: 0; }
}
`;

/* ── Enhanced Interval Chart with zones, gap markers, focus mode ───────── */
const IntervalChart = ({ intervalData, medianInterval, officialLines }) => {
  const [focusMode, setFocusMode] = useState(false);

  // Zone boundaries (relative to median)
  const suspectThresh = medianInterval * 1.8;
  const missedThresh  = medianInterval * 2.5;
  const overcountThresh = medianInterval * 0.4;

  // Focus mode: crop Y-axis to typical range, outliers clamped at top
  const focusCeil = medianInterval * 2.8;
  const yDomain = focusMode ? [0, focusCeil] : [0, 'auto'];

  // Counts for header badges
  const missedCount = intervalData.filter(d => d.status === 'missed').length;
  const suspectCount = intervalData.filter(d => d.status === 'suspect').length;
  const overcountCount = intervalData.filter(d => d.status === 'overcount').length;

  // Custom dot renderer with GAP labels + pulsing rings
  const renderDot = ({ cx, cy, payload, index }) => {
    if (cx == null || cy == null) return null;
    const st = payload.status;
    const clr = st === 'missed' ? T.error
      : st === 'suspect' ? T.warning
      : st === 'overcount' ? T.purple
      : st === 'first' ? T.cyan
      : T.success;
    const r = st === 'first' ? 6 : st === 'ok' ? 2.5 : 5;

    // In focus mode, clamp outliers to the top edge
    const isClamped = focusMode && payload.interval > focusCeil;
    const finalCy = isClamped ? 18 : cy;

    return (
      <g key={`dot-${index}`}>
        {/* Pulsing ring animation for missed strokes */}
        {st === 'missed' && (
          <circle cx={cx} cy={finalCy} r={5} fill="none" stroke={T.error} strokeWidth={2} opacity={0.6}>
            <animate attributeName="r" from="5" to="16" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.7" to="0" dur="1.5s" repeatCount="indefinite" />
          </circle>
        )}
        {/* Main dot */}
        <circle cx={cx} cy={finalCy} r={r} fill={clr} stroke="#fff" strokeWidth={1.5} />
        {/* GAP label above missed dots */}
        {st === 'missed' && (
          <text x={cx} y={finalCy - 12} textAnchor="middle" fontSize={8} fontWeight={800}
            fill={T.error} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            GAP
          </text>
        )}
        {/* Outlier icon when clamped in focus mode */}
        {isClamped && (
          <text x={cx} y={finalCy - 2} textAnchor="middle" fontSize={11}>
            ⚠️
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="bg-white rounded-2xl border p-5" style={{ borderColor: T.border }}>
      {/* Inject pulsing keyframe */}
      <style>{pulseStyle}</style>

      {/* Header with focus toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Timer size={16} style={{ color: T.intervalLine }} />
          <h2 className="text-sm font-black" style={{ color: T.text }}>
            Intervalo entre Braçadas
          </h2>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: T.warningBg, color: T.warning }}>
            Med: {fmtShort(medianInterval)}
          </span>
          {missedCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: T.errorBg, color: T.error }}>
              {missedCount} gap{missedCount > 1 ? 's' : ''}
            </span>
          )}
          {suspectCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: T.warningBg, color: T.warning }}>
              {suspectCount} suspeito{suspectCount > 1 ? 's' : ''}
            </span>
          )}
          {overcountCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: T.purpleBg, color: T.purple }}>
              {overcountCount} extra{overcountCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={() => setFocusMode(!focusMode)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all"
          style={{
            backgroundColor: focusMode ? T.accent : T.accentSoft,
            color: focusMode ? '#fff' : T.textSoft,
          }}>
          <Target size={11} />
          {focusMode ? 'Foco ON' : 'Foco'}
        </button>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={intervalData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border} />

          {/* ── Background zone shading ──────────────────── */}
          {/* Green zone: ideal rhythm (overcount threshold to median) */}
          <ReferenceArea
            y1={overcountThresh} y2={medianInterval}
            fill={T.success} fillOpacity={0.06}
            ifOverflow="hidden"
          />
          {/* Neutral zone: median to suspect */}
          <ReferenceArea
            y1={medianInterval} y2={suspectThresh}
            fill={T.warning} fillOpacity={0.04}
            ifOverflow="hidden"
          />
          {/* Yellow zone: suspect to missed */}
          <ReferenceArea
            y1={suspectThresh} y2={missedThresh}
            fill={T.warning} fillOpacity={0.08}
            ifOverflow="hidden"
          />
          {/* Red zone: above missed threshold */}
          <ReferenceArea
            y1={missedThresh} y2={focusMode ? focusCeil : 999}
            fill={T.error} fillOpacity={0.08}
            ifOverflow="hidden"
          />

          <XAxis
            dataKey="timeS"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => fmtTime(v)}
            tick={{ fontSize: 10, fill: T.textMute }}
            label={{ value: 'Tempo da prova', position: 'insideBottomRight', offset: -5, fontSize: 10, fill: T.textMute }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: T.textMute }}
            label={{ value: 'Intervalo (s)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: T.textMute }}
            domain={yDomain}
            allowDataOverflow={focusMode}
          />
          <Tooltip content={<ChartTooltip type="interval" />} />

          {/* Median reference */}
          <ReferenceLine y={medianInterval} stroke={T.accent} strokeDasharray="6 3" strokeWidth={1.5}
            label={{ value: `Med ${fmtShort(medianInterval)}`, position: 'right', fontSize: 9, fill: T.accent, fontWeight: 700 }} />

          {/* Suspect threshold */}
          <ReferenceLine y={suspectThresh} stroke={T.warning} strokeDasharray="3 3" strokeWidth={1}
            label={{ value: 'Suspeito', position: 'right', fontSize: 8, fill: T.warning }} />

          {/* Missed threshold */}
          <ReferenceLine y={missedThresh} stroke={T.error} strokeDasharray="3 3" strokeWidth={1}
            label={{ value: 'Perdida', position: 'right', fontSize: 8, fill: T.error }} />

          {/* Overcount threshold */}
          <ReferenceLine y={overcountThresh} stroke={T.purple} strokeDasharray="2 4" strokeWidth={0.8}
            label={{ value: 'Extra', position: 'left', fontSize: 7, fill: T.purple }} />

          {/* Official reference lines */}
          {officialLines.map((ol, i) => (
            <ReferenceLine key={i} x={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1}
              label={{ value: ol.label, position: 'top', fontSize: 8, fill: ol.color }} />
          ))}

          {/* Interval line + custom dots */}
          <Line type="monotone" dataKey="interval" name="Intervalo"
            stroke={T.intervalLine} strokeWidth={2}
            dot={renderDot}
            activeDot={{ r: 7, stroke: T.accent, strokeWidth: 2, fill: '#fff' }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px]" style={{ color: T.textMute }}>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: T.cyan }} /> Saída→1ª
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: T.success }} /> OK
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: T.warning }} /> Suspeito (&gt;1.8× med)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full inline-block border-2" style={{ backgroundColor: T.error, borderColor: T.error }} />
          <span style={{ color: T.error }}>Perdida (&gt;2.5× med)</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: T.purple }} /> Extra (&lt;0.4× med)
        </span>
        <span className="mx-1 text-[8px] opacity-50">|</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: T.success, opacity: 0.15 }} /> Zona ideal
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: T.error, opacity: 0.2 }} /> Zona de gap
        </span>
      </div>
    </div>
  );
};


/* ── Stroke-by-Stroke Table ────────────────────────────────────────────────── */
const StrokeTable = ({ strokes, raceOrigin, medianInterval, startToFirst }) => {
  const [expanded, setExpanded] = useState(false);
  const displayStrokes = expanded ? strokes : strokes.slice(0, 12);

  const classify = (iv, idx) => {
    if (idx === 0) return { label: 'SAÍDA', color: T.cyan, bg: T.cyanBg };
    if (iv == null) return { label: '—', color: T.textMute, bg: T.accentSoft };
    if (iv > medianInterval * 2.5) return { label: 'PERDIDA', color: T.error, bg: T.errorBg };
    if (iv > medianInterval * 1.8) return { label: 'SUSPEITO', color: T.warning, bg: T.warningBg };
    if (iv < medianInterval * 0.4) return { label: 'EXTRA', color: T.purple, bg: T.purpleBg };
    return { label: 'OK', color: T.success, bg: T.successBg };
  };

  return (
    <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: T.border }}>
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: T.border }}>
        <div className="flex items-center gap-2">
          <Waves size={14} style={{ color: T.textSoft }} />
          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
            Braçada a Braçada
          </span>
        </div>
        <span className="text-[10px]" style={{ color: T.textMute }}>{strokes.length} eventos</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: T.accentSoft }}>
              <th className="px-3 py-2 text-left font-bold" style={{ color: T.textSoft }}>#</th>
              <th className="px-3 py-2 text-left font-bold" style={{ color: T.textSoft }}>Tempo Vídeo</th>
              <th className="px-3 py-2 text-left font-bold" style={{ color: T.textSoft }}>Tempo Prova</th>
              <th className="px-3 py-2 text-left font-bold" style={{ color: T.textSoft }}>Intervalo</th>
              <th className="px-3 py-2 text-left font-bold" style={{ color: T.textSoft }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {displayStrokes.map((s, i) => {
              const iv = i === 0
                ? (startToFirst != null ? startToFirst : (s.videoTimeS - raceOrigin))
                : s.intervalS;
              const cls = classify(iv, i);
              const raceT = s.videoTimeS - raceOrigin;
              return (
                <tr key={s.number} className="border-b hover:bg-gray-50"
                  style={{ borderColor: T.border, backgroundColor: cls.label !== 'OK' && cls.label !== '—' ? cls.bg + '40' : undefined }}>
                  <td className="px-3 py-2 font-bold" style={{ color: T.text }}>{s.number}</td>
                  <td className="px-3 py-2 font-mono" style={{ color: T.textSoft }}>{fmtTime(s.videoTimeS)}</td>
                  <td className="px-3 py-2 font-mono" style={{ color: T.text }}>{fmtTime(raceT)}</td>
                  <td className="px-3 py-2 font-mono font-bold" style={{ color: cls.color }}>{fmtShort(iv)}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold"
                      style={{ backgroundColor: cls.bg, color: cls.color }}>
                      {cls.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {strokes.length > 12 && (
        <button onClick={() => setExpanded(!expanded)}
          className="w-full py-2.5 text-[10px] font-bold hover:bg-gray-50 transition-colors"
          style={{ color: T.accent, borderTop: `1px solid ${T.border}` }}>
          {expanded ? 'Mostrar menos' : `Ver todas ${strokes.length} braçadas`}
        </button>
      )}
    </div>
  );
};

export default RaceDashboardPage;
